<template>
  <div class="d-flex align-items-center">
    <group-recipients-listable-manager
      ref="groupRecipients"
      :gsearch="search"
      @input="onGroupsUpdated"
      @depleted="groupsDepleted = true"
      @incomplete="groupsDepleted = false"
    />
    <customer-recipients-listable-manager
      v-if="customersFetchFlag"
      ref="customerRecipients"
      :csearch="validationSearch !== null ? validationSearch : search"
      @input="onCustomersUpdated"
      @depleted="customersFetchFlag = false"
    />
    <custom-vue-tags-input
      ref="vueTagsInput"
      v-model="newRecipient"
      class="vue-tags-input--clean"
      placeholder
      :tags="tags"
      :autocomplete-items="autocompleteElements"
      :add-from-paste="false"
      :autocomplete-min-length="MIN_SEARCH_LENGTH"
      :autocomplete-filter-duplicates="false"
      :avoid-adding-duplicates="false"
      :validation="validation"
      :add-on-key="[13,',', ':', ';']"
      @before-adding-tag="preAddTagVerification"
      @tags-changed="onTagsChanged"
      @input="onVueTagsInput"
      @scroll="onScroll"
    >
      <div
        slot="autocomplete-item"
        slot-scope="props"
        class="my-item"
        :class="`${props.item.group &&
          props.item.object &&
          props.item.object.is_supergroup ?
            'bg-secondary font-weight-bold' : ''}`"
        @click="props.performAdd(props.item)"
      >
        <span v-if="props.item.group">
          <font-awesome-icon icon="users" />
          {{ props.item.object.name }}
        </span>
        <span
          v-else
          :class="`${props.item.object.unsubscribed ? 'unsubscribed-text' : ''}`"
        >
          {{ getTagName(props.item) }} {{ props.item.object.unsubscribed ? ' (Unsubscribed)':'' }}
          <base-text
            v-if="getTagName(props.item) !== props.item.object.email"
            light
            inline
            class="autocomplete__email"
          >— {{ props.item.object.email }}</base-text>
        </span>
      </div>
      <div
        slot="tag-center"
        slot-scope="props"
        @click="props.performOpenEdit(props.index)"
      >
        <base-tooltip
          v-if="props.tag.object && props.tag.object.email && props.tag.object.first_name"
          :text="props.tag.object.email"
        >
          {{ getTagName(props.tag) }}
        </base-tooltip>
        <span v-else>
          {{ getTagName(props.tag) }}
        </span>
      </div>
      <!-- icon group -->
      <!-- <div
        slot="tag-left"
        slot-scope="props"
      >
        <span
          v-if="props.tag.group"
          class="mr-2"
        >
          <font-awesome-icon icon="users" />
        </span>
      </div> -->
    </custom-vue-tags-input>
  </div>
</template>
<script>

/*
  This component uses listable twice: once for customer groups and once for
  customers, and it does so through the GroupRecipientsListableManager and
  CustomerRecipientsListableManager components.   (via 72792e977)
*/

import CustomVueTagsInput from '@/components/CustomVueTagsInput';
import _ from 'lodash';
import { EventBus } from '@/event-bus';

const MIN_SEARCH_LENGTH = 1;

export default {
  name: 'EmailRecipientsEditor',
  components: {
    CustomVueTagsInput,
  },
  // mixins: [Listable],
  props: {
    item: {
      type: Object,
      required: true,
    },
    recipients: {
      type: Array,
      required: true,
    },
    showGroups: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      customersFetchFlag: false,
      newRecipient: '', // text input content
      search: '',
      groups: [],
      customers: [],
      manualTags: [],
      groupsDepleted: false,
      MIN_SEARCH_LENGTH,
    };
  },
  computed: {
    /** vue-tags-parsed version of customers and groups. */
    autocompleteElements() {
      const alreadyCustomerPks = this.recipients.filter((c) => !c.group).map((c) => c.object.pk);
      // remove already on edit recipients
      const customers = this.customers.map((c) => this.customerToTag(c))
        .filter((c) => !alreadyCustomerPks.includes(c.object.pk));

      const alreadyGroupPks = this.recipients.filter((g) => g.group).map((g) => g.object.pk);
      // Remove already on edit group recipients
      const groups = !this.showGroups ? [] : this.groups.map((g) => this.groupToTag(g))
        .filter((g) => !alreadyGroupPks.includes(g.object.pk));
      return [...groups, ...customers];
    },
    validation() {
      return [
        {
          classes: 'min-length',
          rule: (tag) => tag.object != null
              && !tag.group
              && (tag.object.first_name == null || tag.object.first_name.length === 0),
        },
      ];
    },
    tags() {
      const realTags = this.recipients.map((e) => {
        if (e.group) e.classes = 'ti-tag--group';
        if (!e.group && !e.object.is_mine) {
          e.classes = 'ti-tag--other';
        }
        return e;
      }).sort((tag1, tag2) => tag2.group - tag1.group);
      return [...realTags, ...this.manualTags];
    },
    isLoading() {
      return this.$store.state.groups.isLoading || this.$store.state.customers.isLoading;
    },
    validationSearch() {
      return this.manualTags.length ? this.manualTags[0].text : null;
    },
  },
  watch: {
    /** When our recipients list is up to date, validate manual tags. */
    isLoading(newVal) {
      if (!newVal && this.validationSearch !== null) {
        this.manualTags = [];
        this.validateManualTags();
      }
    },
    groupsDepleted(newVal) {
      this.customersFetchFlag = newVal;
    },
    search() {
      if (this.groupsDepleted) this.customersFetchFlag = true;
    },
  },
  mounted() {
    if (!this.showGroups) {
      this.customersFetchFlag = true;
    }
  },
  methods: {
    onSearch: _.debounce(function (evt) {
      this.search = evt;
    }, 200),
    validateManualTags() {
      if (this.manualTags.length === 0) return;
      // Should 99% times be just 1 element
      this.manualTags.forEach((tag, index) => {
        const manualInput = tag.text.toLowerCase().trim();

        // const autocompleteMatch = this.autocompleteElements.find((t) => this.checkMatch(t, manualInput));
        // if (autocompleteMatch) {
        //   if (!this.verify(autocompleteMatch)) {
        //     this.manualTags.splice(index, 1);
        //     return;
        //   }
        //   // Existing manual tags get replaced by recipient object
        //   this.manualTags.splice(index, 1);
        //   // Behaves as if user had selected the tag from the autocomplete list
        //   this.onTagsChanged([...this.recipients, autocompleteMatch]);
        //   return;
        // }

        // silently remove if already in recipients
        if (this.alreadyAdded(manualInput)) {
          this.manualTags.splice(index, 1);
          return;
        }

        const vm = this;
        EventBus.$emit('confirm', {
          title: 'Error',
          message: `<b><u>${manualInput}</u></b> is not one of your assigned clients.`,
          positiveText: 'OK',
          showCancel: false,
          callback: (confirm) => {
            if (confirm) {
              vm.manualTags = [];
            }
          },
          onClose: () => {
            vm.manualTags = [];
          },
        });
        this.search = '';
      });
    },
    alreadyAdded(manualInput) {
      return this.recipients.filter((r) => this.checkMatch(r, manualInput)).length > 0;
    },
    checkMatch(tag, input) {
      const emailMatches = !tag.group && tag.object?.email?.toLowerCase().trim() === input.toLowerCase().trim();
      const nameMatches = tag.text?.toLowerCase().trim().includes(input.toLowerCase().trim());
      return emailMatches || nameMatches;
    },
    customerToTag(customer) {
      return {
        object: customer,
        group: false,
        text: customer.first_name ? (`${customer.first_name} ${customer.last_name}`) : customer.email,
        name: customer.last_name,
      };
    },
    groupToTag(group) {
      return {
        object: group,
        group: true,
        text: group.name,
        name: group.name,
      };
    },
    onCustomersUpdated(items) {
      this.customers = items;
    },
    onGroupsUpdated(items) {
      const groups = [];
      items.forEach((item) => {
        if (!groups.find((i) => i.pk === item.pk)) groups.push(item);
      });
      this.groups = groups;
    },
    // eslint-disable-next-line func-names
    onVueTagsInput: _.debounce(function (str) {
      if (str?.length > 0 && str?.length < MIN_SEARCH_LENGTH) return;
      this.search = str;
    }, 300, { leading: true }),
    preAddTagVerification(tagObject) {
      if (tagObject?.tag?.object ?? false) {
        if (!this.verify(tagObject.tag)) {
          return;
        } else {
          tagObject.addTag();
        }
      } else {
        // Adds the user input when not in autocomplete list
        tagObject.addTag();
      }
      this.newRecipient = '';
    },
    verify(tag) {
      // Checks if the customer to be added is unsubscribed; if it is, it doesn't add the customer to recipients
      if (!tag.group && tag.object.unsubscribed) {
        this.search = '';
        return false;
      }
      // Checks if the group has any members on it, otherwise it shows an error message
      if (tag.group && tag.object.member_count === 0) {
        EventBus.$emit('toast-error', 'This group has no clients in it. Please select a different client or group, or add clients to this group first.');
        this.search = '';
        return false;
      }
      return true;
    },
    onTagsChanged(tags) {
      // Tags with actual recipient data
      // eslint-disable-next-line no-prototype-builtins
      const savedTags = tags.filter((t) => t.hasOwnProperty('object'));
      // Tags without recipient data
      // eslint-disable-next-line no-prototype-builtins
      this.manualTags = tags.filter((t) => !t.hasOwnProperty('object')).map((t) => ({
        group: false,
        invalid: true,
        text: t.text,
        name: null,
        classes: 'ti-tag--invalid',
      }));

      const toCustomers = savedTags.filter((x) => !x.group).map((x) => x.object);
      const toGroups = savedTags.filter((x) => x.group).map((x) => x.object);

      const customersChanged = !_.isEqual(toCustomers.map((c) => c.pk).sort(),
        this.recipients.filter((r) => !r.group).map((tag) => tag.object.pk).sort());
      const groupsChanged = !_.isEqual(toGroups.map((g) => g.pk).sort(),
        this.recipients.filter((r) => r.group).map((tag) => tag.object.pk).sort());
      if (!customersChanged && !groupsChanged) {
        // Nothing changed => Nothing to emit or dispatch yet.
        return;
      }

      this.$emit('recipients-change', savedTags);

      this.$store.dispatch('entries/updateTo', {
        toCustomers,
        toGroups,
      }).then(() => {
        /** Close dropdown menu after a recipient is added */
        this.$nextTick().then(() => {
          this.$refs.vueTagsInput.blur();
        });
        this.$emit('save');
        if (this.groupsDepleted) this.customersFetchFlag = true;
      }).catch((e) => {
        console.error(e);
      });
    },
    getTagName(tag) {
      if (tag.invalid) return tag.text;
      const {
        object: {
          // eslint-disable-next-line camelcase
          first_name, last_name, email,
        },
        text,
        name,
      } = tag;
      // Try first and last name
      // eslint-disable-next-line camelcase
      return ((first_name && first_name.length) || (last_name && last_name.length)) ? `${first_name} ${last_name}`
      // Try email
        : email && email.length ? email
        // try 'text'
          : text && text.length ? text
          // try 'name'
            : name && name.length ? name
              : 'unknown'; // should NEVER happen
    },
    /** Autocomplete list scroll. Fetch all groups first. Start fetching customers later. */
    // TODO: Implement with v-scrollable directive inside CustomVueTagsInput
    // eslint-disable-next-line
    onScroll: _.debounce(function (evt) {
      const threshold = 10; // sometimes, no threshold results in 1px away from bottom.
      const { scrollTop, clientHeight, scrollHeight } = evt.target;
      const reachedBottom = scrollTop + clientHeight + threshold >= scrollHeight;
      if (!reachedBottom) return;
      this.$nextTick().then(() => {
        this.$refs[this.customersFetchFlag ? 'customerRecipients' : 'groupRecipients'].loadMore();
      });
    }, 50),
  },
};
</script>
<style type="scss" scoped>
.unsubscribed-text {
  color: #ADADAD;
}
</style>
<style lang="scss">
.vue-tags-input .ti-tag.min-length {
  background: $gray-light;
  color: $black;
}

.vue-tags-input__label {
  margin-left: 11px;
  color: #495057;
}

.vue-tags-input--clean {
  width: 100%;
  max-width: 100% !important;

  .ti-input {
    border: none !important;
  }

  .ti-selected-item,
  .ti-tag {
    background-color: $clementine !important;
    color: $black !important;
    border-radius: 4px;
    padding: 2.5px 4px 2.5px 8px;
    height: 24px;

    .ti-content{
      font-size: 14px;
      line-height: 135%;
      color: #1B1B1B;
      font-weight: 300;
      font-family: 'SilkaLight';
    }

    &--group {
      //$dusty-rose
      background-color: #fff !important;
      border: 1px solid #D6D6D6;
    }
    &--other {
      background-color: #E2E2E2 !important;
    }
    &--invalid {
      background-color: #DA1E28 !important;
      color: white !important;
      .ti-content{
        color: white !important;
      }
    }
  }
  .ti-autocomplete {
    max-width: 400px;
    max-height: 572px;
    overflow-y: auto;
    border: 1px solid #ccc;
    @include nested-scrollbars;

  }
}
.autocomplete__email{
  color: $gray;
}
</style>
